import jsPDF from 'jspdf';

// Logo URL
const LOGO_URL = "https://drlp.atlantic4web.pt/logo-drleonardopacheco.jpg";

// Format date for display
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Helper function to load image as base64
const loadImageAsBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/jpeg'));
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
};

// Helper to print PDF (opens print dialog)
export const printPDF = (doc) => {
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  const printWindow = window.open(pdfUrl, '_blank');
  if (printWindow) {
    printWindow.addEventListener('load', () => {
      setTimeout(() => {
        printWindow.print();
      }, 500);
    });
  }
};

// Helper to download PDF (kept for backward compatibility)
export const downloadPDF = (doc, filename) => {
  doc.save(filename);
};

// Generate Receita PDF - Multi-page support (5 products per page)
export const generateReceitaPDF = async (receita, cliente, definicoes) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);
  const productsPerPage = 5;
  const totalProducts = receita.produtos?.length || 0;
  const totalPages = Math.max(1, Math.ceil(totalProducts / productsPerPage));

  let logoBase64 = null;
  try {
    logoBase64 = await loadImageAsBase64(LOGO_URL);
  } catch (e) {
    // Logo not available
  }

  for (let page = 0; page < totalPages; page++) {
    if (page > 0) doc.addPage();

    // Header title
    doc.setFillColor(240, 240, 240);
    doc.rect(0, 0, pageWidth, 15, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(50, 50, 50);
    doc.text('Guia de Tratamento - Receita Médica', pageWidth / 2, 10, { align: 'center' });

    // Main border
    doc.setDrawColor(100, 100, 100);
    doc.setLineWidth(0.5);
    doc.rect(margin, 22, contentWidth, 255);

    // Logo section
    if (logoBase64) {
      doc.addImage(logoBase64, 'JPEG', margin + 5, 30, 40, 0);
    } else {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(180, 146, 42);
      doc.text(definicoes?.nome_empresa || 'Dr. Leonardo Pacheco', margin + 5, 42);
    }

    // Right side box
    doc.setDrawColor(100, 100, 100);
    doc.setLineWidth(0.3);
    doc.rect(pageWidth - margin - 65, 34, 60, 15);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(50, 50, 50);
    doc.text('Guia de Tratamento para o Utente', pageWidth - margin - 62, 40);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('Documento para o paciente', pageWidth - margin - 62, 46);

    // Receita title with number
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(`Guia de Tratamento da Receita N.º ${receita.numero_receita}`, margin + 5, 68);

    // Horizontal line
    doc.setDrawColor(150, 150, 150);
    doc.setLineWidth(0.2);
    doc.line(margin + 5, 72, pageWidth - margin - 5, 72);

    // Patient info section
    const infoStartY = 80;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);

    doc.text('Utente:', margin + 5, infoStartY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(cliente?.nome || receita.cliente_nome, margin + 25, infoStartY);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text('NIF:', margin + 5, infoStartY + 8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(cliente?.nif || '-', margin + 25, infoStartY + 8);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text('Morada:', margin + 5, infoStartY + 16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    const moradaCompleta = [cliente?.morada, cliente?.codigo_postal, cliente?.cidade].filter(Boolean).join(', ') || '-';
    doc.text(moradaCompleta.substring(0, 60), margin + 25, infoStartY + 16);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text('Data de prescrição:', pageWidth / 2 + 10, infoStartY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(formatDate(receita.created_at), pageWidth / 2 + 50, infoStartY);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text('Telefone:', pageWidth / 2 + 10, infoStartY + 8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(cliente?.telemovel || '-', pageWidth / 2 + 50, infoStartY + 8);

    // Horizontal line before table
    doc.setDrawColor(150, 150, 150);
    doc.line(margin + 5, infoStartY + 25, pageWidth - margin - 5, infoStartY + 25);

    // Table header
    const tableStartY = infoStartY + 32;
    doc.setFillColor(245, 245, 245);
    doc.rect(margin + 5, tableStartY - 5, contentWidth - 10, 10, 'F');

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(50, 50, 50);
    doc.text('Rx', margin + 8, tableStartY + 2);
    doc.text('Descrição do Produto / Posologia', margin + 18, tableStartY + 2);
    doc.text('Qtd.', pageWidth - margin - 25, tableStartY + 2);

    // Table rows - 5 per page
    let rowY = tableStartY + 12;
    const startIdx = page * productsPerPage;
    const endIdx = Math.min(startIdx + productsPerPage, totalProducts);
    const maxDescWidth = contentWidth - 50;

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30, 30, 30);

    for (let i = startIdx; i < Math.max(endIdx, startIdx + productsPerPage); i++) {
      const displayNum = (i - startIdx) + 1;

      // Row number box
      doc.setDrawColor(150, 150, 150);
      doc.setLineWidth(0.2);
      doc.rect(margin + 5, rowY - 4, 8, 16);
      doc.setFontSize(8);
      doc.setTextColor(30, 30, 30);
      doc.text(String(i + 1), margin + 8, rowY + 4);

      if (receita.produtos && receita.produtos[i]) {
        const produto = receita.produtos[i];

        // Product name
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 30, 30);
        doc.text(produto.produto_nome, margin + 18, rowY + 2);

        // Observation - no character limit, wrap text with proper line breaks
        if (produto.observacao) {
          doc.setFontSize(7);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(80, 80, 80);
          const obsLines = doc.splitTextToSize(produto.observacao, maxDescWidth);
          doc.text(obsLines.slice(0, 3), margin + 18, rowY + 8);
          doc.setTextColor(30, 30, 30);
        }

        // Quantity
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 30, 30);
        doc.text(String(produto.quantidade), pageWidth - margin - 22, rowY + 4);
      }

      // Row bottom line
      doc.setDrawColor(200, 200, 200);
      doc.line(margin + 5, rowY + 14, pageWidth - margin - 5, rowY + 14);

      rowY += 18;
    }

    // Signature area (below the table) - line for signing + label
    const signatureY = rowY + 10;
    doc.setDrawColor(150, 150, 150);
    doc.setLineWidth(0.3);
    doc.line(pageWidth - margin - 70, signatureY + 20, pageWidth - margin - 5, signatureY + 20);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text('Assinatura / Carimbo', pageWidth - margin - 38, signatureY + 26, { align: 'center' });

    // Footer - contact info below border (left), receita ID (right)
    const borderBottom = 277; // bottom of main border (22 + 255)
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    const contactParts = [definicoes?.telefone_empresa, definicoes?.email_empresa].filter(Boolean).join(' | ');
    if (contactParts) {
      doc.text(contactParts, margin, borderBottom + 6);
    }
    doc.text(receita.numero_receita, pageWidth - margin, borderBottom + 6, { align: 'right' });

    // Page number
    if (totalPages > 1) {
      doc.setFontSize(7);
      doc.text(`Pág. ${page + 1} / ${totalPages}`, pageWidth / 2, borderBottom + 6, { align: 'center' });
    }
  }

  return doc;
};

// Calculate etiqueta content height using a temporary doc
const calcEtiquetaHeight = (produto, cliente, receita) => {
  const tmp = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [140, 200] });
  const pageWidth = 140;
  const pad = 3;
  const maxW = pageWidth - (pad * 2);
  let y = 11;

  // Title
  tmp.setFontSize(14);
  tmp.setFont('helvetica', 'bold');
  y += 6;

  // Client
  tmp.setFontSize(13);
  tmp.setFont('helvetica', 'bold');
  const cLines = tmp.splitTextToSize((cliente?.nome || receita.cliente_nome).toUpperCase(), maxW);
  y += cLines.length * 5.5;

  // Date
  y += 5;

  // Product
  tmp.setFontSize(13);
  tmp.setFont('helvetica', 'normal');
  const pLines = tmp.splitTextToSize(produto.produto_nome, maxW);
  y += pLines.length * 5.5;

  // Observation
  if (produto.observacao) {
    tmp.setFontSize(13);
    tmp.setFont('helvetica', 'bold');
    const oLines = tmp.splitTextToSize(produto.observacao, maxW);
    y += oLines.length * 5.5 + 2;
  }

  return y + 3; // small bottom padding
};

// Draw etiqueta content on a page
const drawEtiquetaPage = (doc, produto, cliente, receita) => {
  const pageWidth = 140;
  const pad = 3;
  const maxW = pageWidth - (pad * 2);

  // Company Name - gold color
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(180, 146, 42);
  doc.text('DR. LEONARDO PACHECO', pad, 6);

  // Client Name - slightly smaller, uppercase, wrap
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 30, 30);
  const clientName = (cliente?.nome || receita.cliente_nome).toUpperCase();
  const clientLines = doc.splitTextToSize(clientName, maxW);
  doc.text(clientLines, pad, 15);

  // Date - bigger, right aligned, below client
  let y = 15 + (clientLines.length * 5.5);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(formatDate(receita.created_at), pageWidth - pad, y, { align: 'right' });

  // Product name - normal weight, bigger
  y += 5;
  doc.setFontSize(13);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(30, 30, 30);
  const nameLines = doc.splitTextToSize(produto.produto_nome, maxW);
  doc.text(nameLines, pad, y);

  // Observation - bold, bigger
  y += nameLines.length * 5.5;
  if (produto.observacao) {
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    const obsLines = doc.splitTextToSize(produto.observacao, maxW);
    doc.text(obsLines, pad, y + 1);
  }
};

// Generate Etiqueta PDF for a single product - dynamic height
export const generateSingleEtiquetaPDF = (produto, cliente, receita, definicoes) => {
  const h = calcEtiquetaHeight(produto, cliente, receita);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [140, h] });
  drawEtiquetaPage(doc, produto, cliente, receita);
  return doc;
};

// Generate Etiqueta PDF - one page per product, each with dynamic height
export const generateEtiquetaPDF = (receita, cliente, definicoes) => {
  const firstProd = receita.produtos[0];
  const firstH = calcEtiquetaHeight(firstProd, cliente, receita);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [140, firstH] });
  drawEtiquetaPage(doc, firstProd, cliente, receita);

  for (let i = 1; i < receita.produtos.length; i++) {
    const prod = receita.produtos[i];
    const h = calcEtiquetaHeight(prod, cliente, receita);
    doc.addPage([140, h], 'landscape');
    drawEtiquetaPage(doc, prod, cliente, receita);
  }

  return doc;
};

// Generate Atestado PDF (Certificate)
export const generateAtestadoPDF = async (receita, cliente, definicoes) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const centerX = pageWidth / 2;

  // Try to load and add logo (centered)
  try {
    const logoBase64 = await loadImageAsBase64(LOGO_URL);
    doc.addImage(logoBase64, 'JPEG', centerX - 30, 15, 60, 0);
  } catch (e) {
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(180, 146, 42);
    doc.text(definicoes?.nome_empresa || 'Dr. Leonardo Pacheco', centerX, 30, { align: 'center' });
  }

  // Title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text('ATESTADO', centerX, 70, { align: 'center' });

  // Decorative line
  doc.setDrawColor(180, 146, 42);
  doc.setLineWidth(0.5);
  doc.line(60, 75, pageWidth - 60, 75);

  // Client Info
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105);

  const clientName = cliente?.nome || receita.cliente_nome;
  const clientNIF = cliente?.nif ? `, NIF: ${cliente.nif}` : '';

  doc.text(`Nome: ${clientName}${clientNIF}`, 30, 95);

  if (cliente?.morada) {
    doc.text(`Morada: ${cliente.morada}`, 30, 105);
  }
  if (cliente?.cidade || cliente?.codigo_postal) {
    doc.text(`${cliente?.codigo_postal || ''} ${cliente?.cidade || ''}`.trim(), 30, 115);
  }

  // Certificate Text
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);

  const textoAtestado = definicoes?.texto_atestado ||
    'Atesta-se para os devidos efeitos que o(a) paciente acima identificado(a) compareceu a este estabelecimento na data indicada para aquisição de medicamentos conforme receita médica apresentada.';

  const lines = doc.splitTextToSize(textoAtestado, pageWidth - 60);
  doc.text(lines, 30, 140);

  // Date
  const currentDate = formatDate(receita.created_at);
  doc.setFontSize(11);
  doc.text(`Data: ${currentDate}`, 30, 180);

  // Signature line
  doc.setDrawColor(200, 200, 200);
  doc.line(pageWidth - 90, 220, pageWidth - 30, 220);
  doc.setFontSize(9);
  doc.setTextColor(148, 163, 184);
  doc.text('Assinatura e Carimbo', pageWidth - 60, 228, { align: 'center' });

  // Reference
  doc.setFontSize(8);
  doc.text(`Ref. Receita: ${receita.numero_receita}`, 30, 270);

  return doc;
};

// Helper to open PDF in new tab
export const openPDF = (doc) => {
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};
